import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { cloneDeep } from "lodash";
import PropTypes from "prop-types";

import useLanguageChange from "../../../hooks/useLanguageChange";

import {
    FROM_WHERE,
    IMAGE_OBJ_KEYS,
    MAX_IMAGE_COUNT_OF_INDEXED_DB,
    PICKED_FILES_FROM,
    SELECTED_SCREEN,
    SUB_SCREEN,
    UPLOADING_FILE_STATUS,
    UPLOADING_STATUS_FOR_TOAST,
    UPLOAD_FILES_DATA,
    UPLOAD_FILE_DATA_TRACK_STATUS,
    UPLOAD_TYPE_PARAM_FOR_UPLOAD_FILES,
} from "../../../constants/UploadFilesConstants";

import { CALL_DATA, GLOBAL_MEETING_DATA, MIME_TYPE, PublicVariables } from "../../../constants/PublicVariables";
import {
    addUploadedFileInThumbList,
    changeIsFromMarkupValue,
    changePreviousScreen,
    changeUploadFileScreen,
    changeUploadFileSubScreen,
    deleteImageById,
    deleteUploadedFileFromThumbList,
    setIsHideFinalAddBtn,
    setUplodedFilesThumbListArray,
    updateImagesArray,
    updatePropertyOfImageAtID,
} from "../../../reducers/uploadFile-reducer";
import { ENABLED } from "../../../constants/Constants";
import { APIs } from "../../../constants/APIConstants";

import uploadFilesService from "../../../services/uploadFilesService";
import watermarkImageService from "../../../services/WatermarkImage/watermarkImageService";
import navigatorService from "../../../services/navigatorService";
import takePhotoService from "../../../services/takePhotoService";
import CustomToast from "../../UI/customToast";
import api from "../../../apis/api";
import {
    isIOSOrMacSafariImageBlobIssue,
    isOfflinePhotoCaptureFeatureAvailable,
    isProfessionalEdition,
    isRemainingFileCountLess,
    spliceAtIndex,
} from "../../../shared/utility";
import googleEvent from "../../../services/googleEvent";
import { EVENT_NAME } from "../../../constants/GTagConstants";
import { LogFirebase } from "../../../services/firestore";
import { FIREBASE_EVENT_CATEGORY, FIREBASE_LOG_KEY, FIREBASE_LOG_VALUE } from "../../../constants/FirebaseConstants";
import { checkIfLocalTrackReattach, checkPublishVideoOnExit } from "../../../shared/dataTrackFunction";
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import { handleEndCallFunc } from "../../Twilio/Room/roomFunction";
import { addMultipleContainerImagesInArray } from "../../../reducers/imageContainer-reducer";
import livePointerService from "../../LivePointer/livePointerService";
import { indexedDBService } from "../../OfflineMode/indexedDBService";
import { setOfflineModeUploadedImageCount } from "../../../reducers/offline-mode-reducer";

const UploadedFiles = ({ updatedSelectedFiles, dispatchJoinMeeting }) => {
    const dispatch = useDispatch();
    const { isOfflineModeActive, offlineModeUploadedImageCount } = useSelector((state) => state.offlineMode);

    const { imagesArray, uploadedFilesThumbList, isHideFinalAddBtn } = useSelector((state) => state.uploadFile);

    const [disablePlusButton, setDisablePlusButton] = useState(
        imagesArray?.some((image) => image.uploadingStatus === UPLOADING_FILE_STATUS.READY_TO_UPLOAD)
    );
    const [isCaptureButtonDisabledForOffline, setIsCaptureButtonDisabledForOffline] = useState(false);
    const [showPlusButton, setShowPlusButton] = useState(true);
    const [uploadedFileMsg, setUploadedFileMsg] = useState("");
    const [showUploadFailedCaption, setShowUploadFailedCaption] = useState(false);
    const [numberOfFailedFileMsg, setNumberOfFailedFileMsg] = useState("");
    const [deletingImageId, setDeletingImageId] = useState(-1);

    /* custome hook for getting updated localized value object for entire project 
    This will give localize value object for selected language
    Also will give updated localized value object when language change*/
    const localisedValues = useLanguageChange();

    const uploadCount = useRef(0);
    const numberOfTotalUploadingImages = useRef(0); //total number of images uploading
    const currentUploadingImage = useRef(0);
    const totalNumberOfRetry = useRef(imagesArray?.filter((image) => image.uploadingStatus === UPLOADING_FILE_STATUS.UPLOAD_FAILED).length);
    const nextStateToCheck = useRef({ imgID: -1, prop: null, newValue: null });
    const thumbList = useRef([]); //Empty array for refering list of images that will be tracked for counter

    useEffect(() => {
        startUpload();
        UPLOAD_FILES_DATA.selectedImageObj = null;
        UPLOAD_FILES_DATA.selectedImageIndex = -1;
        UPLOAD_FILES_DATA.pickedFilesFrom = PICKED_FILES_FROM.NONE;
    }, []);

    useEffect(() => {
        if (imagesArray.length === 0) {
            uploadComplete(true);
        } else if (imagesArray.length > 0 && nextStateToCheck.current.retryAll) {
            startUpload();
        } else if (imagesArray.length > 0 && nextStateToCheck.current.imgID >= 0) {
            const selectedImgObj = imagesArray.find((img) => img[IMAGE_OBJ_KEYS.ID] === nextStateToCheck.current.imgID);
            if (selectedImgObj && selectedImgObj[nextStateToCheck.current.prop] === nextStateToCheck.current.newValue) {
                processForNextImage();
                nextStateToCheck.current = { imgID: -1, prop: null, newValue: null };
            }
        }

        totalNumberOfRetry.current = getCountByState(UPLOADING_FILE_STATUS.UPLOAD_FAILED);
        remainingFileCountHandle(0);
        if (isProfessionalEdition() && isHideFinalAddBtn) {
            setShowPlusButton(false);
        }

        if (isOfflineModeActive) {
            const isAllImageLoadingOver = imagesArray.every((img) =>
                [UPLOADING_FILE_STATUS.UPLOAD_SUCCESS, UPLOADING_FILE_STATUS.UPLOAD_FAILED].includes(img.uploadingStatus)
            );
            if (isAllImageLoadingOver) {
                checkIndexedDBImage(); // Check pluse button show/hide case
            }
        }
    }, [imagesArray, isOfflineModeActive]);

    const startUpload = () => {
        compressAndUploadFiles();
        getUploadedFileMsg();
    };

    const processForNextImage = () => {
        const readyToUploadImageID = getImageIDByStatus(UPLOADING_FILE_STATUS.READY_TO_UPLOAD);
        if (readyToUploadImageID >= 0) {
            uploadImageInQueue(readyToUploadImageID);
            return null;
        } else {
            const retrySingleImageID = getImageIDByStatus(UPLOADING_FILE_STATUS.RETRY_SINGLE);
            if (retrySingleImageID >= 0) {
                retryUploadFailedClickHandler(retrySingleImageID);
            }
        }
    };

    const getImageIDByStatus = (fileStatus) => {
        const imageIDWithStatus = getPropertyByStatus(fileStatus, IMAGE_OBJ_KEYS.ID);
        return imageIDWithStatus;
    };

    //#region event handlers
    const doneWithUploadFilesHandler = () => {
        uploadComplete();
    };

    const reconnectClickHandler = () => {
        dispatchJoinMeeting();
    };

    const uploadComplete = (isByDelete = false) => {
        googleEvent.logEvent(EVENT_NAME.UPLOAD_FILE_DONE_TAPPED);

        if (imagesArray.length > 0) dispatch(updateImagesArray([])); //reset images array
        dispatch(changePreviousScreen(SELECTED_SCREEN.OFF_VIEW)); //change previous screen as no screen

        if (isByDelete) {
            dispatch(changeUploadFileScreen(SELECTED_SCREEN.SELECT_FILES));
        } else {
            if (GLOBAL_MEETING_DATA.ucid) {
                //add all images to Agent thumb container
                uploadedImageInAgentThumbContainer();
            }
            LogFirebase(FIREBASE_EVENT_CATEGORY.UploadFiles, FIREBASE_LOG_KEY.SendMessage, FIREBASE_LOG_VALUE.UploadFileEndNotification);
            dispatch(changeUploadFileScreen(SELECTED_SCREEN.OFF_VIEW));

            if (PublicVariables.isOnlyUploadImageMeet) {
                //end call
                handleEndCallFunc();
            } else {
                uploadFilesService.sendTrackOnUploadFileStartEnd(UPLOAD_FILE_DATA_TRACK_STATUS.END, uploadedFilesThumbList);
                uploadFilesService.onExitUploadFiles(GLOBAL_MEETING_DATA.markupLiveStream);
                checkPublishVideoOnExit(); //// case - 1 video is on before upload file
                checkIfLocalTrackReattach();
                // Use video-ready detection instead of fixed timeout
                livePointerService.waitForVideoReadyThenExecute(() => {
                    // enableLivePointerForMobile sends ENABLED status and auto-starts pointer after
                    livePointerService.enableLivePointerForMobile("after_upload_files_complete");
                });
            }

            thumbList.current = []; //reset thumb list
            dispatch(setUplodedFilesThumbListArray([]));
        }
    };

    // uploaded image show in Agent thumb container
    const uploadedImageInAgentThumbContainer = () => {
        takePhotoService.uploadedImage.imgThumb = "";
        takePhotoService.uploadedImage.imgOrigin = "";
        takePhotoService.uploadedImage.imgFileName = "";

        if (uploadedFilesThumbList.length > 0) {
            dispatch(addMultipleContainerImagesInArray(uploadedFilesThumbList));
            const lastThumbImage = uploadedFilesThumbList[uploadedFilesThumbList.length - 1];
            takePhotoService.uploadedImage.imgThumb = lastThumbImage.thumbURL;
            takePhotoService.uploadedImage.imgOrigin = lastThumbImage.image_url;
            takePhotoService.uploadedImage.imgFileName = lastThumbImage.imageName;
        }
    };

    const deleteImageClickHandler = (imgId) => {
        setDeletingImageId(imgId);
        googleEvent.logEvent(EVENT_NAME.UPLOAD_FILE_DELETE_TAPPED);
    };

    const retryUploadFailedClickHandler = (imgId) => {
        if (currentUploadingImage.current === 0) {
            dispatch(updatePropertyOfImageAtID({ imgID: imgId, prop: IMAGE_OBJ_KEYS.UPLOADING_STATUS, newValue: UPLOADING_FILE_STATUS.UPLOADING }));
            setShowUploadFailedCaption(false);
            setDisablePlusButton(true);
            numberOfTotalUploadingImages.current++;
            const compressedImageObj = getPropertyByID(imgId, IMAGE_OBJ_KEYS.COMPRESSED_IMAGE);
            uploadCompressedImage(imgId, compressedImageObj, UPLOADING_STATUS_FOR_TOAST.RETRY);
        } else {
            dispatch(
                updatePropertyOfImageAtID({ imgID: imgId, prop: IMAGE_OBJ_KEYS.UPLOADING_STATUS, newValue: UPLOADING_FILE_STATUS.RETRY_SINGLE })
            );
        }
    };

    const retryUploadAllFailedClickHandler = () => {
        /* retry uploading failed images */
        let updatedImageArray = cloneDeep(imagesArray);

        updatedImageArray.map((img) => {
            if (img[IMAGE_OBJ_KEYS.UPLOADING_STATUS] === UPLOADING_FILE_STATUS.UPLOAD_FAILED) {
                img[IMAGE_OBJ_KEYS.UPLOADING_STATUS] = UPLOADING_FILE_STATUS.READY_TO_UPLOAD;
            }

            return img;
        });

        dispatch(updateImagesArray(updatedImageArray));
        setDisablePlusButton(true);
        setShowUploadFailedCaption(false);

        nextStateToCheck.current = { retryAll: true };
        currentUploadingImage.current = 0;
        totalNumberOfRetry.current = 0;

        googleEvent.logEvent(EVENT_NAME.UPLOAD_FILE_RETRYALL_TAPPED);
    };

    const preventEvent = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const dragChange = (e, pickedFilesFrom = PICKED_FILES_FROM.NONE) => {
        preventEvent(e);
        const allFiles = Array.from(e.target.files);

        UPLOAD_FILES_DATA.pickedFilesFrom = pickedFilesFrom;
        dispatch(changeIsFromMarkupValue(false));

        if (allFiles.length > 0) uploadFilesService.generateFilesArray(allFiles, updatedSelectedFiles);
    };

    const fromDeviceClickHandler = () => {
        document.getElementById("file-upload-from-final-view")?.click();
    };

    const fromCameraClickHandler = () => {
        if (PublicVariables.isMobile) {
            /* add mobile evnt handler code here */
            if (isOfflineModeActive) {
                document.getElementById("cameraFileInput_uploadedFile_for_offline_mode")?.click();
            } else {
                document.getElementById("cameraFileInput_UploadedFiles")?.click();
            }
        } else {
            dispatch(changeUploadFileSubScreen(SUB_SCREEN.CAMERA_VIEW));
        }
    };
    //#endregion
    const getCountByState = (imgStatus) => {
        const uploadingStatusCount = imagesArray.filter((image) => image?.uploadingStatus === imgStatus).length;
        return uploadingStatusCount;
    };

    const getPropertyByStatus = (imgStatus, prop) => {
        const imgByStatus = imagesArray.find((img) => img.uploadingStatus == imgStatus);
        if (imgByStatus) {
            return imgByStatus[prop];
        } else {
            switch (prop) {
                case IMAGE_OBJ_KEYS.ID:
                    return -1;

                case IMAGE_OBJ_KEYS.EXTENSION:
                    return "jpeg";

                default:
                    return null;
            }
        }
    };

    const getPropertyByID = (imgId, prop) => {
        const imgById = imagesArray.find((img) => img.id === imgId);
        if (imgById) {
            return imgById[prop];
        } else {
            switch (prop) {
                case IMAGE_OBJ_KEYS.ID:
                    return -1;

                case IMAGE_OBJ_KEYS.SIZE:
                    return 0;

                case IMAGE_OBJ_KEYS.SOURCE:
                case IMAGE_OBJ_KEYS.UPDATED_SOURCE:
                case IMAGE_OBJ_KEYS.COMPRESSED_IMAGE:
                    return null;

                case IMAGE_OBJ_KEYS.CAPTION:
                case IMAGE_OBJ_KEYS.EXTENSION:
                case IMAGE_OBJ_KEYS.TYPE:
                    return "";

                case IMAGE_OBJ_KEYS.LATITUDE:
                case IMAGE_OBJ_KEYS.LONGITUDE:
                    return "0";

                case IMAGE_OBJ_KEYS.UPLOADING_STATUS:
                    return UPLOADING_FILE_STATUS.NOTHING;

                default:
                    return null;
            }
        }
    };

    const removeSrcData = (imgId, compressedImageData) => {
        if (getPropertyByID(imgId, IMAGE_OBJ_KEYS.TYPE) === FROM_WHERE.GALLERY) {
            URL.revokeObjectURL(getPropertyByID(imgId, IMAGE_OBJ_KEYS.SOURCE));
        }

        dispatch(updatePropertyOfImageAtID({ imgID: imgId, prop: IMAGE_OBJ_KEYS.COMPRESSED_IMAGE, newValue: compressedImageData }));
        dispatch(updatePropertyOfImageAtID({ imgID: imgId, prop: IMAGE_OBJ_KEYS.SOURCE, newValue: null }));
    };

    const addWatermark = (newImg, imgId, mime_type) => {
        return new Promise((resolve) => {
            try {
                const deviceOrientation = navigatorService.getOrientation();
                const { imageSrc, canvasHeight } = watermarkImageService.__watermarkImage.fetchImageWaterMark({
                    screenType: "upload-File",
                    width: newImg.width,
                    height: newImg.height,
                    localOrientation: deviceOrientation,
                    bookmark: "",
                    liveLat: getPropertyByID(imgId, IMAGE_OBJ_KEYS.LATITUDE),
                    liveLong: getPropertyByID(imgId, IMAGE_OBJ_KEYS.LONGITUDE),
                    localSS: false, //localSS
                    localVideo: false, //localvideo
                });

                const watermarkImg = new Image();
                let watermarkCanvas = document.getElementById("screenshotcanvas");
                let watermarkContext = watermarkCanvas.getContext("2d");
                watermarkCanvas.width = newImg.width;
                watermarkCanvas.height = canvasHeight;
                watermarkImg.onload = () => {
                    watermarkContext.drawImage(newImg, 0, 0);
                    watermarkContext.drawImage(watermarkImg, 0, 0);
                    const watermarkImageData = watermarkCanvas.toDataURL(mime_type);
                    resolve(watermarkImageData);
                };

                watermarkImg.src = imageSrc;
            } catch (error) {
                resolve(false);
            }
        });
    };

    const compressImage = (imgId) => {
        return new Promise((resolve) => {
            const compressedImageObj = getPropertyByID(imgId, IMAGE_OBJ_KEYS.COMPRESSED_IMAGE);
            if (compressedImageObj) {
                resolve(compressedImageObj);
            } else {
                dispatch(
                    updatePropertyOfImageAtID({ imgID: imgId, prop: IMAGE_OBJ_KEYS.UPLOADING_STATUS, newValue: UPLOADING_FILE_STATUS.COMPRESSING })
                );
                const mime_type = getPropertyByID(imgId, IMAGE_OBJ_KEYS.EXTENSION) === "png" ? MIME_TYPE.PNG : MIME_TYPE.JPEG;
                const compressionPercentage = uploadFilesService.getCompressionPercentage(getPropertyByID(imgId, IMAGE_OBJ_KEYS.SIZE));
                if (compressionPercentage > 0) {
                    try {
                        const newImg = new Image();
                        newImg.extension = getPropertyByID(imgId, IMAGE_OBJ_KEYS.EXTENSION);
                        newImg.onload = () => {
                            const compressionCanvas = document.createElement("canvas");
                            const compressionImage = document.createElement("img");
                            compressionImage.src = newImg.src;
                            compressionCanvas.width = newImg.width;
                            compressionCanvas.height = newImg.height;
                            const context = compressionCanvas.getContext("2d");

                            setTimeout(() => {
                                context.drawImage(compressionImage, 0, 0);
                                compressionCanvas.toBlob(() => {
                                    compressionCanvas.remove();
                                    compressionImage.remove();
                                    addWatermark(newImg, imgId, mime_type)
                                        .then((response) => resolve(response))
                                        .catch((error) => resolve(false));
                                });
                            }, 100);
                        };

                        newImg.src = getPropertyByID(imgId, IMAGE_OBJ_KEYS.UPDATED_SOURCE);
                    } catch (error) {
                        resolve(false);
                    }
                } else {
                    const newImg = new Image();
                    newImg.extension = getPropertyByID(imgId, IMAGE_OBJ_KEYS.EXTENSION);
                    newImg.onload = () => {
                        addWatermark(newImg, imgId, mime_type)
                            .then((response) => resolve(response))
                            .catch((error) => resolve(false));
                    };
                    newImg.src = getPropertyByID(imgId, IMAGE_OBJ_KEYS.UPDATED_SOURCE);
                }
            }
        });
    };

    const savePhotoToDB = (imageData) => {
        return indexedDBService.storeImage(imageData);
    };

    const offlineModePhotosUpload = (imgId, fileName, blob, callback) => {
        const latValue = getPropertyByID(imgId, IMAGE_OBJ_KEYS.LATITUDE);
        const longValue = getPropertyByID(imgId, IMAGE_OBJ_KEYS.LONGITUDE);
        const caption = getPropertyByID(imgId, IMAGE_OBJ_KEYS.CAPTION);
        LogFirebase("OfflineMode", "image_upload_queue", { imgId, fileName, latValue, longValue, caption });
        const imageData = {
            id: imgId,
            name: fileName,
            imgData: blob,
            uploadedAt: new Date().toISOString(),
            uploaded: false,
            sessionId: GLOBAL_MEETING_DATA.sessionId,
            lat: latValue,
            long: longValue,
            caption: caption,
        };
        savePhotoToDB(imageData)
            .then(() => {
                callback();
            })
            .catch(() => {
                callback();
            });
    };

    const uploadQueuingImage = (useImageFor, imgId, compressedImageData, callback) => {
        const imageExtension = "jpeg";
        uploadCount.current++;
        let blob = watermarkImageService.__base64ToBlobImage.convertData(compressedImageData);
        const fileName = takePhotoService.getFileName(useImageFor, uploadCount.current, imageExtension);
        if (isOfflineModeActive) {
            if (isIOSOrMacSafariImageBlobIssue()) {
                LogFirebase("OfflineMode", "upload_QueuingImage_for_iOS", { base64_data: "true" });
                blob = compressedImageData;
            }
            offlineModePhotosUpload(imgId, fileName, blob, callback);
        } else {
            const fileObj = new File([blob], fileName, { type: "image/jpeg" });
            const formData = new FormData();
            formData.append("file", fileObj);
            const featureFlag = PublicVariables.featureFlag;
            formData.append(
                "folderName",
                featureFlag?.FOLDERNAMEFORMAT && featureFlag?.ENABLEFOLDERNAMING === ENABLED ? featureFlag.FOLDERNAMEFORMAT : ""
            );

            if (featureFlag?.WATERMARKONPHOTOS === ENABLED) {
                const latValue = getPropertyByID(imgId, IMAGE_OBJ_KEYS.LATITUDE);
                const longValue = getPropertyByID(imgId, IMAGE_OBJ_KEYS.LONGITUDE);
                if (latValue != undefined && latValue != null && latValue != "" && latValue != 0.0 && latValue != 0)
                    formData.append("latitude", latValue);
                if (longValue != undefined && longValue != null && longValue != "" && longValue != 0.0 && longValue != 0)
                    formData.append("longitude", longValue);
            }

            formData.append("fileName", fileName);
            formData.append("sessionId", GLOBAL_MEETING_DATA.sessionId);
            formData.append("fileType", "img");
            const caption = getPropertyByID(imgId, IMAGE_OBJ_KEYS.CAPTION);
            if (caption) {
                formData.append("caption", caption);
            }
            formData.append("fromGallery", UPLOAD_TYPE_PARAM_FOR_UPLOAD_FILES); // extra request params pass for the photo upload

            const uploadFileUrl = APIs.UPLOAD_SESSION_FILE;
            api.sendImageRequest(uploadFileUrl, formData, {})
                .then((result) => {
                    callback(result);
                })
                .catch((err) => {
                    callback(err);
                });
        }
    };

    const remainingFileCountHandle = (increment, reset) => {
        GLOBAL_MEETING_DATA.remainingFileCount = reset ? 0 : GLOBAL_MEETING_DATA.remainingFileCount + increment;
        getUploadedFileMsg();
    };

    // uploaded image show in Agent thumb container
    const uploadMultipleQueuePhoto = (imgId, compressedImageData, statusForUploading, callback) => {
        uploadingToastMessage(statusForUploading);
        uploadQueuingImage("image", imgId, compressedImageData, (response) => {
            uploadingToastMessage(UPLOADING_STATUS_FOR_TOAST.COMPLETE);
            if (!isOfflineModeActive) {
                const result = response.data;
                if (response.success > 0) {
                    const thumbImage = { thumbURL: result.thumbUrl, image_url: result.fileUrl, imageName: result.fileName, caption: result.caption };
                    thumbList.current.push(thumbImage);
                    dispatch(addUploadedFileInThumbList(thumbImage));
                    const update = { fileName: result.fileName };
                    /* decrease remaining file count by 1 */
                    remainingFileCountHandle(-1);
                    processAfterUploadingCompleted(localisedValues?.successfully_uploaded);
                    callback(true, update);
                } else {
                    totalNumberOfRetry.current++;
                    processAfterUploadingCompleted(localisedValues?.upload_failed);
                    if (result?.error?.errorcode === 1008) {
                        /* reset remaining file count */
                        remainingFileCountHandle(0, true);
                    }
                    callback(false);
                }
            } else {
                processAfterUploadingCompleted(localisedValues?.successfully_uploaded);
                callback(true);
            }
        });
    };

    const processAfterUploadingCompleted = (toastMessage) => {
        console.log("totalNumberOfRetry", numberOfTotalUploadingImages.current, currentUploadingImage.current);
        if (numberOfTotalUploadingImages.current === currentUploadingImage.current) {
            numberOfTotalUploadingImages.current = 0;
            currentUploadingImage.current = 0;
            takePhotoService.showUploadingStatus(toastMessage, true);
            setTimeout(() => {
                setDisablePlusButton(false);
                if (totalNumberOfRetry.current > 0) {
                    console.log("totalNumberOfRetry", totalNumberOfRetry.current);
                    setFailedFileMsg();
                    setShowUploadFailedCaption(true);
                }
            }, 2000);
        }
    };

    const setFailedFileMsg = () => {
        const fileCountMsg = localisedValues?.upload_failed_file_count;
        setNumberOfFailedFileMsg(fileCountMsg.replace("<numberOfFailedFiles>", totalNumberOfRetry.current));
    };

    const compressAndUploadFiles = () => {
        numberOfTotalUploadingImages.current = getCountByState(UPLOADING_FILE_STATUS.READY_TO_UPLOAD);

        const readyToUploadImageID = getImageIDByStatus(UPLOADING_FILE_STATUS.READY_TO_UPLOAD);
        if (readyToUploadImageID >= 0) uploadImageInQueue(readyToUploadImageID);
    };

    const uploadingToastMessage = (statusForUploading) => {
        if (statusForUploading === UPLOADING_STATUS_FOR_TOAST.COMPLETE) {
            currentUploadingImage.current++;
        }

        const uploadImageStatus = `${localisedValues?.uploading_files} (${numberOfTotalUploadingImages.current !== currentUploadingImage.current ? currentUploadingImage.current + 1 : currentUploadingImage.current}/${numberOfTotalUploadingImages.current})`;
        takePhotoService.showUploadingStatus(
            uploadImageStatus,
            statusForUploading === UPLOADING_STATUS_FOR_TOAST.COMPLETE && numberOfTotalUploadingImages.current === currentUploadingImage.current
        );
    };

    const getUploadedFileMsg = () => {
        if (isProfessionalEdition()) {
            setUploadedFileMsg(
                localisedValues?.uploaded_files_of_a_maximum
                    .replace("<successCount>", uploadedFilesThumbList.length)
                    .replace("<totalCount>", uploadedFilesThumbList.length + GLOBAL_MEETING_DATA.remainingFileCount)
            );
        }
    };

    const uploadCompressedImage = (imgId, compressedImageData, statusForUploading) => {
        dispatch(changePreviousScreen(SELECTED_SCREEN.FINAL_UPLODING_VIEW));

        /* on uploading response, update uploading status */
        uploadMultipleQueuePhoto(imgId, compressedImageData, statusForUploading, (response, update) => {
            const value = response === true ? UPLOADING_FILE_STATUS.UPLOAD_SUCCESS : UPLOADING_FILE_STATUS.UPLOAD_FAILED;
            let nextState = { imgID: imgId, prop: IMAGE_OBJ_KEYS.UPLOADING_STATUS, newValue: value };
            dispatch(updatePropertyOfImageAtID(nextState));
            if (update) {
                nextState = { imgID: imgId, prop: IMAGE_OBJ_KEYS.FILE_NAME, newValue: update.fileName };
                dispatch(updatePropertyOfImageAtID(nextState));
            }
            nextStateToCheck.current = nextState;
        });
    };

    const uploadImageInQueue = (readyToUploadImageID) => {
        const compressedImageObj = getPropertyByID(readyToUploadImageID, IMAGE_OBJ_KEYS.COMPRESSED_IMAGE);
        if (compressedImageObj) {
            // Add code for uploading compressed image
            // This is case for uploading failed image
            uploadCompressedImage(readyToUploadImageID, compressedImageObj, UPLOADING_STATUS_FOR_TOAST.UPLOADING);
        } else {
            compressImage(readyToUploadImageID).then((response) => {
                removeSrcData(readyToUploadImageID, response);
                uploadCompressedImage(readyToUploadImageID, response, UPLOADING_STATUS_FOR_TOAST.UPLOADING);
            });
        }
    };

    const showRetryButtonOnImage = (currentStatus) => {
        return [UPLOADING_FILE_STATUS.UPLOAD_FAILED].includes(currentStatus);
    };

    const showCloseButtonOnImage = (currentStatus) => {
        return [UPLOADING_FILE_STATUS.UPLOAD_SUCCESS, UPLOADING_FILE_STATUS.UPLOAD_FAILED].includes(currentStatus);
    };

    const showLoaderOnImage = (currentStatus) => {
        return [
            UPLOADING_FILE_STATUS.COMPRESSING,
            UPLOADING_FILE_STATUS.UPLOADING,
            UPLOADING_FILE_STATUS.DELETING_FILE,
            UPLOADING_FILE_STATUS.RETRY_SINGLE,
            UPLOADING_FILE_STATUS.READY_TO_UPLOAD,
        ].includes(currentStatus);
    };

    const closeDeleteImageModalHandler = () => {
        setDeletingImageId(-1);
    };

    const removeUploadFailedImageAtId = (imageId) => {
        /* Remove upload failed image from array only */
        const updatedImageArray = spliceAtIndex(imagesArray, imageId);
        dispatch(updateImagesArray(updatedImageArray));
        totalNumberOfRetry.current--;
        if (totalNumberOfRetry.current <= 0) {
            setShowUploadFailedCaption(false);
        } else {
            setFailedFileMsg();
        }

        if (isProfessionalEdition()) {
            if (isHideFinalAddBtn) {
                dispatch(setIsHideFinalAddBtn(false));
            }
            setShowPlusButton(true);
        }

        CustomToast.success(localisedValues?.image_deleted);
    };

    const removeUploadedImageAtId = (imageId) => {
        /* Remove upload success image from server and then from array */
        dispatch(
            updatePropertyOfImageAtID({
                imgID: imageId,
                prop: IMAGE_OBJ_KEYS.UPLOADING_STATUS,
                newValue: UPLOADING_FILE_STATUS.DELETING_FILE,
            })
        );

        const deleteImageUrl = APIs.DELETE_IMAGE + GLOBAL_MEETING_DATA.sessionId;
        const request = {
            MemberId: CALL_DATA.memberId,
            Files: [
                {
                    name: getPropertyByID(imageId, IMAGE_OBJ_KEYS.FILE_NAME),
                    uploadtype: UPLOAD_TYPE_PARAM_FOR_UPLOAD_FILES.toString(),
                },
            ],
        };

        if (CALL_DATA.memberToken) {
            request.headers = {
                token: CALL_DATA.memberToken,
            };
        }

        api.deleteStorage(deleteImageUrl, request)
            .then((response) => {
                if (response.success > 0) {
                    const imageName = getPropertyByID(imageId, IMAGE_OBJ_KEYS.FILE_NAME);
                    thumbList.current = thumbList.current.filter((img) => img.imageName !== imageName);

                    dispatch(deleteImageById(imageId));
                    dispatch(deleteUploadedFileFromThumbList(imageName));

                    if (isProfessionalEdition()) {
                        if (isHideFinalAddBtn) {
                            dispatch(setIsHideFinalAddBtn(false));
                        }
                        setShowPlusButton(true);
                        remainingFileCountHandle(1);
                    }

                    CustomToast.success(localisedValues?.image_deleted);
                } else {
                    deleteFailedToast();
                }
            })
            .catch((err) => {
                deleteFailedToast();
            });
    };

    const confirmDeleteImageHandler = () => {
        if (deletingImageId >= 0) {
            if (getPropertyByID(deletingImageId, IMAGE_OBJ_KEYS.UPLOADING_STATUS) === UPLOADING_FILE_STATUS.UPLOAD_FAILED) {
                removeUploadFailedImageAtId(deletingImageId);
            } else if (isOfflineModeActive) {
                indexedDBService.deleteImage(deletingImageId).then((imageId) => {
                    LogFirebase("OfflineMode", "delete_image_from_preview", { imageId });
                    const imageName = getPropertyByID(imageId, IMAGE_OBJ_KEYS.FILE_NAME);
                    thumbList.current = thumbList.current.filter((img) => img.imageName !== imageName);
                    dispatch(deleteImageById(imageId));
                    dispatch(deleteUploadedFileFromThumbList(imageName));
                    dispatch(setOfflineModeUploadedImageCount(offlineModeUploadedImageCount - 1));
                    CustomToast.success(localisedValues?.image_deleted);
                });
            } else {
                removeUploadedImageAtId(deletingImageId);
            }
            setDeletingImageId(-1);
        }
    };

    const checkIndexedDBImage = async () => {
        const images = await indexedDBService.getAllImages();
        LogFirebase("OfflineMode", "check_indexed_DB_store_image_count_for_final_screen_pluse_button", { count: images?.length });
        if (images.length >= MAX_IMAGE_COUNT_OF_INDEXED_DB) {
            setIsCaptureButtonDisabledForOffline(true);
        } else {
            setIsCaptureButtonDisabledForOffline(false);
        }
    };

    const deleteFailedToast = () => {
        dispatch(
            updatePropertyOfImageAtID({
                imgID: deletingImageId,
                prop: IMAGE_OBJ_KEYS.UPLOADING_STATUS,
                newValue: UPLOADING_FILE_STATUS.UPLOAD_SUCCESS,
            })
        );
        CustomToast.info(localisedValues?.failed_to_delete_image);
    };

    const headSubtext = isRemainingFileCountLess() ? localisedValues?.uploaded_max_file : uploadedFileMsg;

    return (
        <>
            <div className={clsx("uploaded-file-preview", isOfflineModeActive && "offline-mode-file-preview")} data-testid='uploadedFilePreview'>
                <div className='uploaded-file-preview-in'>
                    <div className='upload-files-head'>
                        <div className='head-text'>{isOfflineModeActive ? localisedValues?.selected_images : localisedValues?.uploaded_files}</div>
                        {!isOfflineModeActive && headSubtext && <div className='head-subtext'>{headSubtext}</div>}
                        {isOfflineModeActive && (
                            <div className='head-subtext'>
                                {localisedValues?.remaining_image_count_label
                                    .replace("<remainingCount>", offlineModeUploadedImageCount)
                                    .replace("<totalCount>", MAX_IMAGE_COUNT_OF_INDEXED_DB)}
                            </div>
                        )}
                    </div>
                    <div className='uploaded-file-list custom-scroll'>
                        <div className='uploaded-file-list-in'>
                            {imagesArray?.map((image) => (
                                <div className='uploaded-file-col' key={image[IMAGE_OBJ_KEYS.ID]}>
                                    {/* Add "file-upload-failed" class for failed image */}
                                    <div
                                        className={clsx(
                                            "uploaded-file-box",
                                            showRetryButtonOnImage(image[IMAGE_OBJ_KEYS.UPLOADING_STATUS]) && "file-upload-failed"
                                        )}
                                    >
                                        <div className='uploaded-file'>
                                            <img
                                                className='uploaded-img'
                                                src={
                                                    image[IMAGE_OBJ_KEYS.COMPRESSED_IMAGE]
                                                        ? image[IMAGE_OBJ_KEYS.COMPRESSED_IMAGE]
                                                        : image[IMAGE_OBJ_KEYS.UPDATED_SOURCE]
                                                }
                                                id={"footerImage-" + image[IMAGE_OBJ_KEYS.ID]}
                                                alt={"footerImage-" + image[IMAGE_OBJ_KEYS.ID]}
                                            />
                                        </div>
                                        {/* Hide this div for uploading image */}
                                        {showCloseButtonOnImage(image[IMAGE_OBJ_KEYS.UPLOADING_STATUS]) && (
                                            <div className='delete-file custom-tooltip tooltip-auto-width m-hide'>
                                                <button
                                                    className='btn delete-btn'
                                                    disabled={isOfflineModeActive && disablePlusButton}
                                                    onClick={() => deleteImageClickHandler(image[IMAGE_OBJ_KEYS.ID])}
                                                >
                                                    <span className='btn-icon icon-close-light'></span>
                                                </button>
                                                <div className='custom-tooltip-content text-nowrap'>
                                                    <div className='custom-tooltip-in'>
                                                        <p>{localisedValues?.delete_lable}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {/* retry button div */}
                                        {showRetryButtonOnImage(image[IMAGE_OBJ_KEYS.UPLOADING_STATUS]) && (
                                            <div className='uploaded-file-retry'>
                                                <div className='button'>
                                                    <button
                                                        className='btn transparent-outline-btn'
                                                        onClick={() => retryUploadFailedClickHandler(image[IMAGE_OBJ_KEYS.ID])}
                                                    >
                                                        <span className='btn-text'>{localisedValues?.retry}</span>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {/* loader on image */}
                                        {showLoaderOnImage(image[IMAGE_OBJ_KEYS.UPLOADING_STATUS]) && (
                                            <div className='uploaded-file-process'>
                                                <div className='round-loader default-loader'></div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {showPlusButton && !(isOfflineModeActive && isCaptureButtonDisabledForOffline) && (
                                <div className='uploaded-file-col'>
                                    <div className={clsx("uploaded-file-box", disablePlusButton && "disable")}>
                                        <div className='dropdown upload-new-file custom-tooltip'>
                                            {!isOfflineModeActive && (
                                                <>
                                                    <button
                                                        className='btn dropdown-toggle triangle-none upload-new-file-btn'
                                                        type='button'
                                                        id='file-upload-dropdown'
                                                        data-bs-toggle='dropdown'
                                                        aria-expanded='false'
                                                        disabled={disablePlusButton}
                                                    >
                                                        <span className='for-icon icon-plus'></span>
                                                    </button>
                                                    <div
                                                        className='dropdown-menu upload-new-file-dropdown-menu'
                                                        aria-labelledby='file-upload-dropdown'
                                                    >
                                                        <div className='dropdown-menu-in'>
                                                            <ul>
                                                                <li>
                                                                    <div className='dropdown-item-div'>
                                                                        <button
                                                                            className='dropdown-item new-file-btn'
                                                                            onClick={() => fromDeviceClickHandler()}
                                                                        >
                                                                            <span className='btn-icon icon-upload-device'></span>
                                                                            <span className='btn-text'>{localisedValues?.from_device}</span>
                                                                        </button>
                                                                        <input
                                                                            className='drag-file-upload-input d-none'
                                                                            id='file-upload-from-final-view'
                                                                            type='file'
                                                                            title=''
                                                                            accept='image/png,image/jpeg,image/jpg'
                                                                            multiple
                                                                            onChange={(e) => dragChange(e, PICKED_FILES_FROM.FROM_GALLERY)}
                                                                            onClick={(e) => {
                                                                                e.target.value = null;
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </li>
                                                                <li>
                                                                    <div className='dropdown-item-div'>
                                                                        <button
                                                                            className='dropdown-item new-file-btn'
                                                                            onClick={() => fromCameraClickHandler()}
                                                                        >
                                                                            <span className='btn-icon icon-camera'></span>
                                                                            <span className='btn-text'>{localisedValues?.from_camera}</span>
                                                                            <input
                                                                                className='d-none'
                                                                                id='cameraFileInput_UploadedFiles'
                                                                                type='file'
                                                                                accept='image/*'
                                                                                capture='camera'
                                                                                onClick={(e) => {
                                                                                    e.target.value = null;
                                                                                }}
                                                                                onChange={(e) => dragChange(e, PICKED_FILES_FROM.FROM_CAMERA)}
                                                                            />
                                                                        </button>
                                                                    </div>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                            {isOfflineModeActive && (
                                                <button
                                                    className='btn dropdown-toggle triangle-none upload-new-file-btn'
                                                    type='button'
                                                    onClick={() => fromCameraClickHandler()}
                                                    disabled={isCaptureButtonDisabledForOffline || disablePlusButton}
                                                >
                                                    <span className='for-icon icon-plus'></span>
                                                    <input
                                                        className='d-none'
                                                        id='cameraFileInput_uploadedFile_for_offline_mode'
                                                        type='file'
                                                        accept='image/*'
                                                        capture='camera'
                                                        onClick={(e) => {
                                                            e.target.value = null;
                                                        }}
                                                        onChange={(e) => dragChange(e, PICKED_FILES_FROM.FROM_CAMERA)}
                                                        disabled={isCaptureButtonDisabledForOffline || disablePlusButton}
                                                    />
                                                </button>
                                            )}
                                            {disablePlusButton && (
                                                <div className='custom-tooltip-content'>
                                                    <div className='custom-tooltip-in'>
                                                        <p>{localisedValues?.image_upload_msg}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className='uploaded-file-foot'>
                        {showUploadFailedCaption && (
                            <div className='upload-failed-msg'>
                                <div className='upload-failed-icon'>
                                    <span className='for-icon icon-warning'></span>
                                </div>
                                <div className='upload-failed-text'>{numberOfFailedFileMsg}</div>
                            </div>
                        )}
                        <div className='button-inline button-inline-tb-space d-flex justify-content-center'>
                            {!isOfflineModeActive && showUploadFailedCaption && (
                                <div className='button'>
                                    <button className='btn btn-outline' onClick={() => retryUploadAllFailedClickHandler()}>
                                        <span className='btn-text'>{localisedValues?.upload_failed_files}</span>
                                    </button>
                                </div>
                            )}
                            {!isOfflineModeActive && (
                                <div className='button'>
                                    <button className='btn btn-primary' disabled={disablePlusButton} onClick={() => doneWithUploadFilesHandler()}>
                                        <span className='btn-text'>
                                            {disablePlusButton ? localisedValues?.uploading_dots : localisedValues?.done}
                                        </span>
                                    </button>
                                </div>
                            )}
                            {isOfflinePhotoCaptureFeatureAvailable() && isOfflineModeActive && (
                                <div className='button'>
                                    <button className='btn btn-action' onClick={reconnectClickHandler} disabled={disablePlusButton}>
                                        <span className='btn-text'>{localisedValues?.reconnect}</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {deletingImageId >= 0 && (
                <DeleteConfirmationModal onCloseDeleteImageModal={closeDeleteImageModalHandler} onConfirmDeleteImage={confirmDeleteImageHandler} />
            )}
        </>
    );
};

UploadedFiles.propTypes = {
    updatedSelectedFiles: PropTypes.func,
};

export default UploadedFiles;
